import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { 
  Typography, 
  Box, 
  CircularProgress, 
  IconButton
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_ENDPOINTS, API_BASE_URL } from "../config/api.config";
import { Tooltip as MuiTooltip } from '@mui/material';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export default function MonthlyProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Recupera i filtri dalla location state o usa valori di default
  const filterYear = location.state?.filterYear || new Date().getFullYear();
  const filterMonth = location.state?.filterMonth || new Date().getMonth() + 1;
  const filterActivityIds = location.state?.filterActivityIds || [];
  
  const [project, setProject] = useState(null);
  const [projectUsers, setProjectUsers] = useState([]);
  const [projectTeams, setProjectTeams] = useState([]);
  const [teamMembershipByUsername, setTeamMembershipByUsername] = useState({});
  const [detailsPivotData, setDetailsPivotData] = useState({});
  const [teamPivotData, setTeamPivotData] = useState({});
  const [selectedTeamsForDetails, setSelectedTeamsForDetails] = useState([]);
  const [teamDetailsLoading, setTeamDetailsLoading] = useState(true);
  
  const isFetchingProjectDetails = useRef(false);
  const lastProjectDetailsKey = useRef(null);
  
  // Carica i dati del progetto
  useEffect(() => {
    if (!projectId) return;
    
    // Carica info progetto
    fetch(`${API_ENDPOINTS.PROJECTS}?project_id=${projectId}`)
      .then(res => res.json())
      .then(data => {
        const projectData = Array.isArray(data.projects) 
          ? data.projects.find(p => p.id === Number(projectId))
          : (data.project || null);
        setProject(projectData);
      })
      .catch(err => console.error('Error loading project:', err));
  }, [projectId]);
  
  // Carica i dettagli del progetto
  useEffect(() => {
    if (!projectId) {
      setTeamDetailsLoading(false);
      setProjectTeams([]);
      return;
    }
    
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const activityIdsKey = (filterActivityIds && filterActivityIds.length > 0) ? filterActivityIds.sort().join(',') : 'all';
    const fetchKey = `${projectId}-${yearMonth}-${activityIdsKey}`;
    
    if (isFetchingProjectDetails.current) {
      return;
    }
    
    if (lastProjectDetailsKey.current === fetchKey) {
      return;
    }
    
    if (lastProjectDetailsKey.current && lastProjectDetailsKey.current !== fetchKey) {
      isFetchingProjectDetails.current = false;
    }
    
    isFetchingProjectDetails.current = true;
    lastProjectDetailsKey.current = fetchKey;
    
    setProjectTeams([]);
    setTeamMembershipByUsername({});
    setTeamDetailsLoading(true);
    
    // Fetch worklog data
    const activityIdsParam = (filterActivityIds && filterActivityIds.length > 0)
      ? `&activity_ids=${encodeURIComponent(filterActivityIds.join(','))}`
      : '';
    fetch(`${API_BASE_URL}/monthly_worklog_summary_user_project?project_id=${encodeURIComponent(projectId)}&year_month=${yearMonth}${activityIdsParam}`)
      .then(res => res.json())
      .then(data => {
        const byUser = {};
        if (Array.isArray(data.data)) {
          data.data.forEach(item => {
            if (!byUser[item.username]) byUser[item.username] = {};
            const day = new Date(item.work_date).getDate();
            byUser[item.username][day] = item;
          });
        }
        setDetailsPivotData(byUser);
      })
      .catch(err => {
        console.error('Error loading details:', err);
        isFetchingProjectDetails.current = false;
      });
    
    // Fetch project users
    fetch(`${API_ENDPOINTS.PROJECT_USERS}?project_id=${encodeURIComponent(projectId)}&include_full_details=true`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.project_users)) {
          const monthStart = new Date(filterYear, filterMonth - 1, 1);
          const validUsers = data.project_users.filter(item => {
            const validityEndDate = item.validity_end_date;
            if (!validityEndDate) return true;
            const endDate = new Date(validityEndDate);
            return endDate >= monthStart;
          });
          
          const formattedUsers = validUsers.map(item => {
            if (item.user) {
              return item.user;
            }
            return item;
          });
          
          setProjectUsers(formattedUsers);
        }
      })
      .catch(err => {
        console.error('Error loading project users:', err);
        isFetchingProjectDetails.current = false;
      });
    
    // Fetch project teams
    const buildTeamMembership = async () => {
      try {
        const teamsRes = await fetch(`${API_ENDPOINTS.TEAMS}?project_id=${encodeURIComponent(projectId)}&include_members=true`);
        if (!teamsRes.ok) {
          setProjectTeams([]);
          setTeamMembershipByUsername({});
          setTeamDetailsLoading(false);
          isFetchingProjectDetails.current = false;
          return;
        }
        const teamsData = await teamsRes.json();
        const teams = teamsData.teams || [];
        setProjectTeams(teams);
        
        const membership = {};
        teams.forEach(team => {
          if (team.members && Array.isArray(team.members)) {
            team.members.forEach(member => {
              const user = member.user || member;
              const username =
                user.username || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email);
              if (!username) return;
              if (!membership[username]) {
                membership[username] = team.name || 'Team';
              }
            });
          }
        });
        setTeamMembershipByUsername(membership);
        isFetchingProjectDetails.current = false;
      } catch (e) {
        console.error('Error loading teams with members:', e);
        setProjectTeams([]);
        setTeamMembershipByUsername({});
        isFetchingProjectDetails.current = false;
      } finally {
        setTeamDetailsLoading(false);
      }
    };
    buildTeamMembership();
  }, [projectId, filterMonth, filterYear, filterActivityIds]);
  
  // Calcola la pivot per Team
  useEffect(() => {
    if (!detailsPivotData || Object.keys(detailsPivotData).length === 0) {
      setTeamPivotData({});
      return;
    }
    const aggregated = {};
    Object.entries(detailsPivotData).forEach(([username, days]) => {
      const teamName = teamMembershipByUsername[username];
      if (!teamName) return;
      if (!aggregated[teamName]) aggregated[teamName] = {};
      Object.entries(days).forEach(([day, item]) => {
        if (!aggregated[teamName][day]) {
          aggregated[teamName][day] = {
            total_effort: 0,
            project_id: item.project_id,
            project_name: item.project_name,
            work_date: item.work_date,
          };
        }
        aggregated[teamName][day].total_effort += Number(item.total_effort) || 0;
      });
    });
    setTeamPivotData(aggregated);
  }, [detailsPivotData, teamMembershipByUsername]);
  
  // Helper per ottenere team validi per il progetto
  const getValidTeamsForProject = (teams, currentProjectId) => {
    if (!currentProjectId) return teams;
    return teams.filter(team => team.project_id === currentProjectId);
  };
  
  // Render tabella pivot per Team
  function renderTeamPivotTable({ teamPivotData, filterYear, filterMonth, selectedProjectForDetails, projectTeams }) {
    const currentProjectId = Number(projectId);
    const validTeams = getValidTeamsForProject(projectTeams || [], currentProjectId);
    
    const hasValidTeams = validTeams.length > 0 || (projectTeams || []).length === 0;
    
    const teamsWithData = teamPivotData ? Object.keys(teamPivotData) : [];
    
    const usersWithoutTeam = hasValidTeams ? Object.keys(detailsPivotData || {}).filter(u => !teamMembershipByUsername[u]) : [];
    const projectUsersWithoutTeam = hasValidTeams ? (projectUsers || [])
      .map(u => u.username || u.name || (u.first_name && u.last_name ? u.first_name + ' ' + u.last_name : u.email))
      .filter(u => !teamMembershipByUsername[u]) : [];
    const hasUsersWithoutTeam = hasValidTeams && (usersWithoutTeam.length > 0 || projectUsersWithoutTeam.length > 0);
    
    const allTeams = [...new Set([
      ...teamsWithData,
      ...(validTeams.map(t => t.name).filter(Boolean)),
      ...(hasUsersWithoutTeam ? ['No team'] : [])
    ])].sort();
    if (allTeams.length === 0) return null;
    
    const daysInMonth = new Date(filterYear, filterMonth, 0).getDate();
    const weekDays = Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      return date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0);
    });
    const weekendIndexes = weekDays.map((wd, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      const day = date.getDay();
      return (day === 0 || day === 6);
    });
    
    const teamTotals = allTeams.map(t => {
      if (t === 'No team') {
        const usersWithoutTeam = Object.keys(detailsPivotData || {}).filter(u => !teamMembershipByUsername[u]);
        return Math.round((usersWithoutTeam.reduce((sum, u) => {
          const userDays = detailsPivotData[u] || {};
          return sum + Object.values(userDays).reduce((daySum, obj) => daySum + (Number(obj?.total_effort) || 0), 0);
        }, 0) / 60) * 100) / 100;
      } else if (teamPivotData && teamPivotData[t]) {
        return Math.round((Object.values(teamPivotData[t]).reduce((a, b) => a + (Number(b?.total_effort) || 0), 0) / 60) * 100) / 100;
      }
      return 0;
    });
    
    const dayTotals = Array.from({ length: daysInMonth }, (_, i) => {
      let total = 0;
      allTeams.forEach(t => {
        if (t === 'No team') {
          const usersWithoutTeam = Object.keys(detailsPivotData || {}).filter(u => !teamMembershipByUsername[u]);
          total += usersWithoutTeam.reduce((sum, u) => {
            const dayData = detailsPivotData[u]?.[i + 1];
            return sum + (Number(dayData?.total_effort) || 0);
          }, 0);
        } else {
          total += Number(teamPivotData?.[t]?.[i + 1]?.total_effort) || 0;
        }
      });
      return Math.round((total / 60) * 100) / 100;
    });
    const grandTotal = Math.round(teamTotals.reduce((a, b) => a + b, 0) * 100) / 100;
    
    // Calcola dimensioni tabella
    const tableDimensions = {
      minWidth: 800,
      firstColWidth: 140,
      dayColWidth: 50,
      totalColWidth: 60
    };
    
    // Funzione per formattare i nomi utente (abbrevia solo la prima parte)
    const formatUserName = (fullName) => {
      if (!fullName || typeof fullName !== 'string') return fullName;
      
      const parts = fullName.trim().split(' ');
      if (parts.length <= 1) return fullName;
      
      // Abbrevia solo la prima parte: prima lettera + punto
      const firstPart = parts[0];
      const abbreviatedFirst = firstPart.charAt(0) + '.';
      
      // Mantieni il resto del nome
      const restOfName = parts.slice(1).join(' ');
      
      return `${abbreviatedFirst} ${restOfName}`;
    };
    
    return (
      <TableContainer component={Paper} sx={{ 
        borderRadius: 2, 
        bgcolor: 'white', 
        p: 0, 
        maxWidth: '100vw', 
        overflowX: 'auto', 
        boxShadow: 'none', 
        border: '1px solid #f3f3f3', 
        mt: 4
      }}>
        <Table size="small" sx={{ 
          minWidth: tableDimensions.minWidth, 
          tableLayout: 'fixed', 
          fontSize: '0.85rem', 
          width: '100%', 
          bgcolor: 'white'
        }}>
          <colgroup>
            <col style={{ width: tableDimensions.firstColWidth }} />
            {Array.from({ length: daysInMonth }).map((_, i) => (
              <col key={i} style={{ width: tableDimensions.dayColWidth }} />
            ))}
            <col style={{ width: tableDimensions.totalColWidth }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} sx={{ 
                bgcolor: '#5ab031', 
                color: 'white', 
                fontWeight: 'normal', 
                fontSize: '0.85rem', 
                border: 'none', 
                borderRight: '0px solid #e0e0e0', 
                textAlign: 'center', 
                minWidth: 120, 
                maxWidth: 160, 
                width: 140,
                position: 'sticky',
                left: 0,
                zIndex: 3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>
                Team
              </TableCell>
              {weekDays.map((wd, i) => (
                <TableCell key={i} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{wd}</TableCell>
              ))}
              <TableCell rowSpan={2} sx={{ 
                bgcolor: '#5ab031', 
                color: 'white', 
                fontWeight: 'normal', 
                fontSize: '0.85rem', 
                p: 0.5, 
                border: '0px solid #f3f3f3', 
                position: 'sticky', 
                right: 0, 
                zIndex: 3, 
                textAlign: 'center',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 3,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>Tot</TableCell>
            </TableRow>
            <TableRow>
              {Array.from({ length: daysInMonth }, (_, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{i + 1}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allTeams.map((t, idx) => (
              <>
              <TableRow
                key={t}
                hover
                sx={{ '&:hover': { bgcolor: '#e6f4ea' }, bgcolor: selectedTeamsForDetails.includes(t) ? '#eef7e9' : undefined }}
                onClick={() => {
                  setSelectedTeamsForDetails(prev => 
                    prev.includes(t) 
                      ? prev.filter(team => team !== t)
                      : [...prev, t]
                  );
                }}
              >
                <MuiTooltip title={selectedTeamsForDetails.includes(t) ? 'Click to collapse' : 'Click to expand'} arrow>
                  <TableCell 
                    className="pivot-user-col"
                    sx={{ 
                      position: 'sticky', 
                      left: 0, 
                      zIndex: 2, 
                      minWidth: 120, 
                      maxWidth: 160, 
                      width: 140, 
                      p: 0.5, 
                      border: '1px solid #f3f3f3', 
                      borderRight: '2px solid #5ab031', 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      textAlign: 'right',
                      bgcolor: 'white',
                      cursor: 'pointer',
                      userSelect: 'none',
                      textDecoration: 'underline',
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      {selectedTeamsForDetails.includes(t) ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                      <span style={{ fontStyle: t === 'No team' ? 'italic' : 'normal' }}>{t}</span>
                    </Box>
                  </TableCell>
                </MuiTooltip>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const dayData = t === 'No team' 
                    ? (() => {
                        const usersWithoutTeam = Object.keys(detailsPivotData || {}).filter(u => !teamMembershipByUsername[u]);
                        let total = 0;
                        usersWithoutTeam.forEach(u => {
                          const userDayData = detailsPivotData[u]?.[i + 1];
                          if (userDayData) total += Number(userDayData.total_effort) || 0;
                        });
                        return total > 0 ? { total_effort: total } : null;
                      })()
                    : teamPivotData?.[t]?.[i + 1];
                  const value = dayData?.total_effort ? Math.round((Number(dayData.total_effort) / 60) * 100) / 100 : '';
                  const isWeekend = weekendIndexes[i];
                  return (
                    <TableCell key={i + 1} sx={{ textAlign: 'center', bgcolor: isWeekend ? 'rgba(55,55,55,0.08)' : 'white', color: '#374151', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', fontWeight: 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums', '&:hover': { bgcolor: '#aace7c78' } }}>
                      {dayData ? (
                        <MuiTooltip
                          title={
                            <span>
                              {dayData.ticket_id && <div><b>Ticket:</b> {dayData.ticket_id}</div>}
                              {dayData.description && <div><b>Desc:</b> {dayData.description}</div>}
                              {dayData.activity_name && <div><b>Activity:</b> {dayData.activity_name}</div>}
                              {dayData.status_name && <div><b>Status:</b> {dayData.status_name}</div>}
                              {dayData.effort_type_name && <div><b>Effort Type:</b> {dayData.effort_type_name}</div>}
                              {dayData.total_effort && <div><b>Effort:</b> {Math.round((Number(dayData.total_effort) / 60) * 100) / 100} h</div>}
                            </span>
                          }
                          arrow
                        >
                          <span>{value}</span>
                        </MuiTooltip>
                      ) : value}
                    </TableCell>
                  );
                })}
                <TableCell sx={{ 
                  bgcolor: 'white !important', 
                  color: 'black !important', 
                  textAlign: 'center', 
                  fontSize: '0.7rem', 
                  p: 0.5, 
                  border: '1px solid #f3f3f3', 
                  borderLeft: '2px solid #5ab031', 
                  position: 'sticky', 
                  right: 0, 
                  zIndex: 2,
                  minWidth: 60,
                  maxWidth: 80,
                  width: 60,
                  fontWeight: 'bold',
                  '@media (max-width: 900px)': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                  }
                }}>{teamTotals[idx]}</TableCell>
              </TableRow>
              {selectedTeamsForDetails.includes(t) && (() => {
                const teamMembers = t === 'No team'
                  ? Object.keys(detailsPivotData || {}).filter(u => !teamMembershipByUsername[u])
                  : Object.keys(detailsPivotData || {}).filter(u => teamMembershipByUsername[u] === t);
                
                if (teamMembers.length === 0) return null;
                
                return teamMembers.map(username => {
                  const userDays = detailsPivotData[username] || {};
                  const userTotal = Object.values(userDays).reduce((sum, obj) => sum + (Number(obj?.total_effort) || 0), 0);
                  return (
                    <TableRow key={username} sx={{ bgcolor: '#e8f5e9' }}>
                      <TableCell sx={{ 
                        fontSize: '0.75rem', 
                        p: 0.5,
                        minWidth: 120,
                        maxWidth: 160,
                        width: 140,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        bgcolor: '#e8f5e9',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        border: '1px solid #f3f3f3',
                        borderRight: '2px solid #5ab031',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}>{formatUserName(username)}</TableCell>
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const dayData = userDays[i + 1];
                        const value = dayData?.total_effort ? Math.round((Number(dayData.total_effort) / 60) * 100) / 100 : '';
                        const isWeekend = weekendIndexes[i];
                        return (
                          <TableCell key={i + 1} sx={{ 
                            textAlign: 'center', 
                            fontSize: '0.7rem', 
                            p: 0.5,
                            bgcolor: isWeekend ? 'rgba(55,55,55,0.08)' : 'transparent',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontVariantNumeric: 'tabular-nums',
                            border: '1px solid #f3f3f3',
                            '&:hover': { bgcolor: '#aace7c78' }
                          }}>
                            {dayData ? (
                              <MuiTooltip
                                title={
                                  <span>
                                    {dayData.ticket_id && <div><b>Ticket:</b> {dayData.ticket_id}</div>}
                                    {dayData.description && <div><b>Desc:</b> {dayData.description}</div>}
                                    {dayData.activity_name && <div><b>Activity:</b> {dayData.activity_name}</div>}
                                    {dayData.status_name && <div><b>Status:</b> {dayData.status_name}</div>}
                                    {dayData.effort_type_name && <div><b>Effort Type:</b> {dayData.effort_type_name}</div>}
                                    {dayData.total_effort && <div><b>Effort:</b> {Math.round((Number(dayData.total_effort) / 60) * 100) / 100} h</div>}
                                  </span>
                                }
                                arrow
                              >
                                <span>{value}</span>
                              </MuiTooltip>
                            ) : value}
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ 
                        textAlign: 'center', 
                        fontSize: '0.7rem', 
                        fontWeight: 'bold', 
                        p: 0.5,
                        minWidth: 60,
                        maxWidth: 80,
                        width: 60,
                        fontVariantNumeric: 'tabular-nums',
                        bgcolor: '#e8f5e9',
                        position: 'sticky',
                        right: 0,
                        zIndex: 2,
                        border: '1px solid #f3f3f3',
                        borderLeft: '2px solid #5ab031',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          right: 0,
                          zIndex: 2,
                          boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}>
                        {Math.round((userTotal / 60) * 100) / 100}
                      </TableCell>
                    </TableRow>
                  );
                });
              })()}
              </>
            ))}
            <TableRow sx={{ bgcolor: '#248b7e' }}>
              <TableCell sx={{ bgcolor: 'white', position: 'sticky', left: 0, zIndex: 2, minWidth: 120, maxWidth: 160, width: 140, p: 0.5, border: '1px solid #f3f3f3', borderRight: '2px solid #5ab031', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'right', borderTop: '2px solid #5ab031' }}>
                Tot
              </TableCell>
              {Array.from({ length: daysInMonth }, (_, i) => (
                <TableCell key={i+1} sx={{ bgcolor: 'white !important', color: 'black !important', textAlign: 'center', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', borderTop: '2px solid #5ab031', fontWeight: 'bold' }}>
                  {dayTotals[i+1]}
                </TableCell>
              ))}
              <TableCell sx={{ bgcolor: 'white !important', color: 'black !important', textAlign: 'center', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', borderLeft: '2px solid #5ab031', borderTop: '2px solid #5ab031', position: 'sticky', right: 0, zIndex: 2, minWidth: 60, maxWidth: 80, width: 60, fontWeight: 'bold' }}>
                {grandTotal}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
  
  const TeamDetailsLoading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
      <CircularProgress size={40} />
      <Typography sx={{ ml: 2 }}>Loading team details...</Typography>
    </Box>
  );
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header con pulsante indietro */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate('/monthly-mng-view', { 
            state: { 
              filterYear, 
              filterMonth, 
              filterActivityIds 
            } 
          })}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {project?.name || 'Project Details'}
        </Typography>
      </Box>
      
      {/* Info progetto */}
      {project && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Project Code:</strong> {project.project_code || 'N/A'}
          </Typography>
          {project.description && (
            <Typography variant="body2">
              <strong>Description:</strong> {project.description}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Period:</strong> {filterMonth}/{filterYear}
          </Typography>
        </Box>
      )}
      
      {/* Tabella dettagli */}
      {teamDetailsLoading ? (
        <TeamDetailsLoading />
      ) : (
        renderTeamPivotTable({ 
          teamPivotData, 
          filterYear, 
          filterMonth, 
          selectedProjectForDetails: project?.name, 
          projectTeams 
        })
      )}
    </Box>
  );
}

